import { BatteryPlus, CalendarSync, KeyRound, MessageSquareText, Scale } from "lucide-react";
import type { ComponentType, ReactNode } from "react";
import { Trans, useTranslation } from "react-i18next";

import { BalanceAside } from "./BalanceAside";
import { useHistorySource } from "./historySource";
import { useSigned } from "./useSigned";
import { CaptainTokensIcon } from "../../constants";
import type { CaptainBillingChangeType, CaptainBillingHistoryEntry, CaptainBillingHistorySource } from "../../types";
import { BalanceSwatch } from "../BalanceSwatch";

import {
  HistoryEntryCard,
  HistoryEntryDispatcher,
  type HistoryEntryVariant,
  type HistoryItemMap,
} from "@/components/ui/v2/history";
import { usePermissionMatrix } from "@/lib/hooks/usePermissionMatrix";
import { useValueFormatter } from "@/utils/useValueFormatter";

export const CAPTAIN_BILLING_HISTORY_PREFIX = "captainBilling.history";

type EntryOf<K extends CaptainBillingChangeType> = Extract<CaptainBillingHistoryEntry, { changeType: K }>;

type Flow = { source: CaptainBillingHistorySource; amount: ReactNode };

function FlowChip({ source, amount }: Flow) {
  const { t } = useTranslation();
  return (
    <span
      data-flow={source}
      className="inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5 text-xs font-medium tabular-nums"
    >
      <BalanceSwatch source={source} />
      {amount}
      <span className="font-normal text-muted-foreground">{t(`${CAPTAIN_BILLING_HISTORY_PREFIX}.balance.${source}`)}</span>
    </span>
  );
}

type BillingEntryCardProps = {
  entry: CaptainBillingHistoryEntry;
  lineKey: string;
  values?: Record<string, string>;
  variant: HistoryEntryVariant;
  icon: ComponentType<{ className?: string }>;
  flows?: Flow[];
  meta?: string;
  delta?: number;
  extra?: ReactNode;
};

function BillingEntryCard({ entry, lineKey, values, variant, icon, flows = [], meta, delta, extra }: BillingEntryCardProps) {
  const { t } = useTranslation();
  const source = useHistorySource();
  const actor = entry.changes.actorName ?? t(`${CAPTAIN_BILLING_HISTORY_PREFIX}.system`);
  const shown = source ? flows.filter((flow) => flow.source === source) : flows;

  return (
    <HistoryEntryCard
      occurredAt={entry.occurredAt}
      variant={variant}
      icon={icon}
      count={meta}
      description={
        <>
          <Trans
            i18nKey={`${CAPTAIN_BILLING_HISTORY_PREFIX}.line.${lineKey}`}
            values={{ actor, ...values }}
            components={{ b: <strong className="font-semibold" /> }}
          />
          {shown.length > 0 && (
            <span className="mt-1.5 flex flex-wrap gap-1.5">
              {shown.map((flow) => (
                <FlowChip key={flow.source} {...flow} />
              ))}
            </span>
          )}
          {extra}
        </>
      }
      aside={<BalanceAside balanceAfter={entry.changes.balanceAfter} delta={delta} />}
    />
  );
}

function ReplyChargedItem({ entry }: { entry: EntryOf<"CaptainReplyCharged"> }) {
  const { canEdit } = usePermissionMatrix();
  const signed = useSigned();
  const source = useHistorySource();
  const { fromCap, fromTopUp, model } = entry.changes;
  const spent: Record<CaptainBillingHistorySource, number> = { cap: fromCap, topUp: fromTopUp };
  const flows = (Object.keys(spent) as CaptainBillingHistorySource[])
    .filter((balance) => spent[balance] > 0)
    .map((balance) => ({ source: balance, amount: signed(-spent[balance]) }));

  return (
    <BillingEntryCard
      entry={entry}
      lineKey="replyCharged"
      variant="update"
      icon={MessageSquareText}
      flows={flows}
      meta={canEdit("captainBilling") ? (model ?? undefined) : undefined}
      delta={-(source ? spent[source] : fromCap + fromTopUp)}
    />
  );
}

function TokensToppedUpItem({ entry }: { entry: EntryOf<"CaptainTokensToppedUp"> }) {
  const signed = useSigned();
  const { tokens } = entry.changes;
  return (
    <BillingEntryCard
      entry={entry}
      lineKey="toppedUp"
      variant="created"
      icon={BatteryPlus}
      flows={[{ source: "topUp", amount: signed(tokens) }]}
      delta={tokens}
    />
  );
}

function MonthlyCapChangedItem({ entry }: { entry: EntryOf<"CaptainMonthlyCapChanged"> }) {
  const { format } = useValueFormatter();
  const { oldCapTokens, newCapTokens } = entry.changes;
  return (
    <BillingEntryCard
      entry={entry}
      lineKey="capChanged"
      variant="update"
      icon={CalendarSync}
      flows={[{ source: "cap", amount: `${format(oldCapTokens, "decimal")} → ${format(newCapTokens, "decimal")}` }]}
      delta={newCapTokens - oldCapTokens}
    />
  );
}

function BalanceCorrectedItem({ entry }: { entry: EntryOf<"CaptainBalanceCorrected"> }) {
  const { t } = useTranslation();
  const signed = useSigned();
  const { requestedTokens, appliedTokens, reason } = entry.changes;
  return (
    <BillingEntryCard
      entry={entry}
      lineKey="corrected"
      variant={appliedTokens < 0 ? "destructive" : "created"}
      icon={Scale}
      flows={appliedTokens !== 0 ? [{ source: "topUp", amount: signed(appliedTokens) }] : []}
      delta={appliedTokens}
      meta={
        appliedTokens !== requestedTokens
          ? t(`${CAPTAIN_BILLING_HISTORY_PREFIX}.meta.correctionClamped`, { requested: signed(requestedTokens) })
          : undefined
      }
      extra={<span className="mt-1.5 block text-muted-foreground italic">“{reason}”</span>}
    />
  );
}

function MonthRolledOverItem({ entry }: { entry: EntryOf<"CaptainMonthRolledOver"> }) {
  const { format } = useValueFormatter();
  const cap = entry.changes.balanceAfter.monthlyCapTokens;
  return (
    <BillingEntryCard
      entry={entry}
      lineKey="rolledOver"
      variant="created"
      icon={CalendarSync}
      flows={cap > 0 ? [{ source: "cap", amount: format(cap, "decimal") }] : []}
    />
  );
}

function AccountOpenedItem({ entry }: { entry: EntryOf<"CaptainTokenAccountOpened"> }) {
  return <BillingEntryCard entry={entry} lineKey="opened" variant="created" icon={CaptainTokensIcon} />;
}

function KeyLinkedItem({ entry }: { entry: EntryOf<"CaptainOpenRouterKeyLinked"> }) {
  const { t } = useTranslation();
  const { workspaceId, keyHash } = entry.changes;
  return (
    <BillingEntryCard
      entry={entry}
      lineKey="keyLinked"
      variant="update"
      icon={KeyRound}
      meta={t(`${CAPTAIN_BILLING_HISTORY_PREFIX}.meta.keyLinked`, { workspace: workspaceId, key: keyHash.slice(-4) })}
    />
  );
}

const CAPTAIN_BILLING_HISTORY_ITEMS: { [K in CaptainBillingChangeType]: ComponentType<{ entry: EntryOf<K> }> } = {
  CaptainTokenAccountOpened: AccountOpenedItem,
  CaptainReplyCharged: ReplyChargedItem,
  CaptainTokensToppedUp: TokensToppedUpItem,
  CaptainMonthlyCapChanged: MonthlyCapChangedItem,
  CaptainBalanceCorrected: BalanceCorrectedItem,
  CaptainMonthRolledOver: MonthRolledOverItem,
  CaptainOpenRouterKeyLinked: KeyLinkedItem,
};

export const CAPTAIN_BILLING_CHANGE_TYPES = Object.keys(CAPTAIN_BILLING_HISTORY_ITEMS) as CaptainBillingChangeType[];

export function CaptainBillingHistoryItem({ entry }: { entry: CaptainBillingHistoryEntry }) {
  return (
    <HistoryEntryDispatcher
      entry={entry}
      items={CAPTAIN_BILLING_HISTORY_ITEMS as HistoryItemMap<CaptainBillingHistoryEntry>}
      i18nPrefix={CAPTAIN_BILLING_HISTORY_PREFIX}
    />
  );
}
